import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  Slice,
} from '@reduxjs/toolkit';

import {RootState} from '@/app/store/store';

import {RolesAccount} from '../auth/constants/AuthConstants';
import {CStatusKYC} from '../auth/constants/KycConstants';
import {IUser} from '../auth/models';

export type KycStatusType = (typeof CStatusKYC)[keyof typeof CStatusKYC];
export type RoleType = (typeof RolesAccount)[keyof typeof RolesAccount];
export interface MetadataPayload {
  key: string;
  value: string | boolean | number;
}
export interface AuthState {
  user: IUser | null;
}

const initialState: AuthState = {
  user: null,
};

export const setProfile = createAsyncThunk<IUser, IUser>(
  'auth/setProfile',
  async user => user,
);

export const resetUser = createAsyncThunk('user/resetUser', async () => {
  return true;
});

export const authSlice: Slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setProfile: (state, action: PayloadAction<IUser>) => {
      state.user = {...action.payload};
    },
    unSetUser: state => {
      state.user = null;
    },
    setCreatedAtUser: (state, action: PayloadAction<Date>) => {
      if (state.user) {
        state.user.userCreatedAt = action.payload;
      }
    },
    setProfileMetadata: (state, action: PayloadAction<unknown>) => {
      if (state.user) {
        state.user.metadata = action.payload;
      }
    },
    setNotifyUser: (state, action: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.notify_me = action.payload;
      }
    },
    setKycStatus: (state, action: PayloadAction<KycStatusType>) => {
      if (state.user) {
        state.user.kycStatus = action.payload;
      }
    },
    setUserMetadata(state, action: PayloadAction<MetadataPayload>) {
      if (!state.user) return;
      state.user = {
        ...state.user,
        metadata: {
          ...(state.user.metadata || {}),
          [action.payload.key]: action.payload.value,
        },
      };
    },
  },
  extraReducers: builder => {
    builder.addCase(setProfile.fulfilled, (state, action) => {
      state.user = action.payload;
    });
  },
});

export const {
  setProfile: setUserAction,
  unSetUser,
  setCreatedAtUser,
  setProfileMetadata,
  setNotifyUser,
  setKycStatus,
  setUserMetadata,
} = authSlice.actions;

export const selectAuthState = (state: RootState): AuthState => state?.auth;
export const selectUser = (state: {auth: AuthState}): IUser | null =>
  state?.auth?.user;
export const selectUserId = (state: {auth: AuthState}): string | undefined =>
  state?.auth?.user?.id;
export const selectRole = (state: {auth: AuthState}): RoleType =>
  state?.auth?.user?.role;
export const selectEmail = (state: {auth: AuthState}): string | undefined =>
  state?.auth?.user?.email;
export const selectMetadataParent = (state: {
  auth: AuthState;
}): Record<string, any> => state?.auth?.user?.metadata;
export const selectIsUserGuest = (state: {auth: AuthState}) => {
  const userRole = state?.auth?.user?.role;
  return !userRole || userRole?.length === 0;
};
export const selectUserRole = (state: {auth: AuthState}) =>
  state?.auth?.user?.role;

export type AuthSliceActions = typeof authSlice.actions;
export default authSlice.reducer;
